import React from "react"
import styled from "@emotion/styled"
import theme from "../styles/theme"

import Layout from "../components/layout"
import MediaFeature from "../components/MediaFeature"
import Quote from "../components/Quote"
import TabbedContent from "../components/TabbedContent"
import SEO from "../components/seo"

const Outer = styled.div`
  padding: 0 32px;
`
const ColourSquareContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 32px;
  align-items: center;
  max-width: ${theme.maxwidth.default};
  margin-left: auto;
  margin-right: auto;
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`

const ColourBlock = styled.div`
  display: grid;
  grid-column: span 2;
  padding: 32px;
  grid-gap: 32px;
  grid-template-columns: repeat(3, 1fr);
  &:nth-of-type(1) {
    background-color: rgba(0, 203, 204, 0.1);
  }
  &:nth-of-type(2) {
    background-color: rgba(255, 169, 136, 0.1);
  }
  &:nth-of-type(3) {
    background-color: rgba(253, 68, 146, 0.1);
  }
  &:nth-of-type(4) {
    background-color: rgba(0, 89, 195, 0.1);
  }
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-column: span 1;
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    grid-column: span 1;
  }
`
const BlockHeader = styled.h2`
  grid-column: span 1;
  grid-row: span 2;
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-column: span 2;
    grid-row: span 1;
  }
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-column: span 1;
    grid-row: span 1;
  }
`
const BlockSubHeader = styled.h2`
  grid-column: span 1;
  grid-row: span 2;
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-column: span 2;
    grid-row: span 1;
  }
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-column: span 1;
    grid-row: span 1;
  }
`

const WhatIsStackPage = () => (
  <Layout>
    <SEO
      title="Sales Tracking & Sales Funnel Software Features"
      description="Stack is your new sales tracking and sales funnel software. Learn to scale your start up with our proven sales methods and build your own sales playbook."
    />
    <MediaFeature
      filename="sale_cycle.png"
      content={
        <>
          <h1>What is Stack?</h1>
          <p>
            <strong>
              Stack gives you the foundation, framework and infrastructure to
              streamline your sales process and scale your business.
            </strong>
          </p>
          <p>
            Having worked with 100+ entrepreneurial businesses and numerous
            VCs/Accelerators, we wanted to make the Stack methodology accessible
            to any business wanting to become enterprise and investment ready
            so, we built our learnings out into a digital product.
          </p>
          <p>
            Stack was created because we wanted to help as many entrepreneurial
            businesses as possible find their feet and grow. Whether directly or
            via investor and accelerator partnerships, we have learned things
            (many times the hard way) that we want to pass along. We know that
            there isn’t a ‘one size fits all’ when it comes to growth, and that
            you are unique, so we have taken the experience garnered, and
            packaged it into a flexible affordable digital platform accessible
            to everyone.
          </p>
          <p>
            We know how expensive consultancy is, so we have decided to do it
            differently. Within Stack you’ll find templates, consultancy type
            guidance, best practices and a toolkit to help you define and
            achieve your growth, scale and sales goals.
          </p>
          <p>
            Stack is not a CRM. It’s not a training system. It’s not an
            outsourced business service. It’s an exciting flexible digital
            product.
          </p>
          <p>
            Be part of a growing network of entrepreneurial companies and move
            your business to the next level with Stack.
          </p>
        </>
      }
    />
    <Outer>
      <ColourSquareContainer>
        <ColourBlock>
          <BlockHeader>Who is stack for?</BlockHeader>
          <div>
            <h4>CEOs and Co-Founders</h4>
            <p>
              Stack is for the people that make the big decisions daily. You’re
              entrepreneurial, ambitious and you want to join a community of
              growing successful businesses.
            </p>
          </div>
          <div>
            <h4>Head of Sales</h4>
            <p>
              Arm yourself with the right tools to support and grow your team.
              Stack helps to build and support your strategy enabling you to
              make the most informed decision possible.
            </p>
          </div>
          <div>
            <h4>Sales Teams</h4>
            <p>
              Stack is for the people that make things happen. Those that want
              transparency in the sales process and strategy to allow them the
              autonomy to succeed.
            </p>
          </div>
          <div>
            <h4>Venture Capital and Accelerators</h4>
            <p>
              Stack is the right choice if you want to give your portfolio
              companies the tools for growth. Leverage our tools and expertise
              during your accelerator program for greater transparency and more
              impactful growth &amp; progress conversations.
            </p>
          </div>
        </ColourBlock>
      </ColourSquareContainer>
    </Outer>
    <Quote
      quote="After following the Stack process, we implemented a sales engine that grew out business 10x YoY. The clear, concise and focused sales process was a big reason we were able to raise our Series A funding the following year."
      position="Rasheed Hammouda"
      name="Co-Founder, CEO, BridgeFT"
      padding
    ></Quote>
    <Outer>
      <ColourSquareContainer>
        <span>
          <h2>How can Stack help my business?</h2>
          <p>
            Within Stack we give you the consultancy guidance with a software
            price. You’ll find templates and best practices that provide you
            with a flexible toolkit to help grow your business.
          </p>
        </span>
        <ColourBlock>
          <BlockSubHeader>Grow your business</BlockSubHeader>
          <div>
            <h4>Attract investors</h4>
            <p>
              Find funding by telling a consistent and professional story of
              business growth. Fuel their excitement and desire to share in your
              success!
            </p>
          </div>
          <div>
            <h4>Strengthen and scale</h4>
            <p>
              If you can accurately predict and prepare for how to sell your
              product, prospects will make a buying decision quicker. Making
              money or selling isn’t linear or easy, but we have found there are
              usually efficiencies to be gained.
            </p>
          </div>
        </ColourBlock>
        <ColourBlock>
          <BlockSubHeader>Align your targets and goals</BlockSubHeader>
          <div>
            <h4>Structure meets strategy</h4>
            <p>
              Create clarity around the direction of the company for both
              internal and external stakeholders, to encourage better internal
              communication have everyone pulling in the same direction on a
              stated &amp; understood set of goals.
            </p>
          </div>
          <div>
            <h4>Reach revenue targets</h4>
            <p>
              Highlight and track progress against business goals and targets,
              so you’re in the best possible position to identify and overcome
              problems early.
            </p>
          </div>
        </ColourBlock>
        <ColourBlock>
          <BlockSubHeader>Define your client and your process</BlockSubHeader>
          <div>
            <h4>Identify your ideal client</h4>
            <p>
              Disqualifying an unsuitable prospect quickly is almost as valuable
              as closing a deal. Smart prospecting tools help you build a
              picture of your ideal client using key characteristics. No time is
              wasted targeting the wrong client.
            </p>
          </div>
          <div>
            <h4>Create a structured sales process</h4>
            <p>
              Create the process stage by stage, deciding what your best
              practice is and how you want to help prospects navigate your sales
              journey to bring them on as a paying client.
            </p>
          </div>
        </ColourBlock>
        <ColourBlock>
          <BlockSubHeader>Manage your time effectively</BlockSubHeader>
          <div>
            <h4>See how time is spent</h4>
            <p>
              Time is a scarce resource, a visual representation allows you to
              see if you and your team are using it wisely.
            </p>
          </div>
          <div>
            <h4>Calculate the costs</h4>
            <p>
              A handy sales calculator allows you to scenario test how many
              clients you need and how that would fit into your sales funnel,
              giving you the metrics to effectively manage your time.
            </p>
          </div>
        </ColourBlock>
      </ColourSquareContainer>
    </Outer>
    <TabbedContent></TabbedContent>
  </Layout>
)

export default WhatIsStackPage
